<script lang="ts" setup generic="T extends { id: string }">
import { computed, ref } from 'vue';
import type { ModalProps } from 'naive-ui';
import type { BasicDialogFormProps, BasicFormExpose } from '@/typings/basic-form';

/** 自定义弹窗表单组件 */
defineOptions({
  name: 'BasicDialogForm'
});
const basicFormRef = ref<BasicFormExpose<T> | null>(null);

const props = withDefaults(defineProps<BasicDialogFormProps<T>>(), {
  modalConfig: () => {
    return {};
  },
  operation: 'view',
  showMessageOnSuccess: true,
  formConfig: () => {
    return {};
  }
});
const emit = defineEmits<{
  save: [Partial<T>];
  update: [Partial<T>];
  close: [];
}>();
/** 显示状态 */
const show = defineModel('show', { type: Boolean, default: false, required: false });
/** 弹窗配置 */
const finalModalConfig = computed<ModalProps>(() => {
  const { modalConfig } = props;
  return {
    blockScroll: true,
    preset: 'dialog',
    closeOnEsc: props.operation === 'view',
    maskClosable: props.operation === 'view',
    resizable: true,
    ...modalConfig
  };
});

/** 标题 */
const finalTitle = computed<string>(() => {
  if (props.customTitle) {
    return props.customTitle;
  }
  if (props.title === undefined) {
    return '';
  }
  let title = '';
  if (typeof props.title === 'function') {
    title = props.title({ data: basicFormRef.value?.model ?? undefined, operation: props.operation });
  } else {
    title = props.title as string;
  }
  switch (props.operation) {
    case 'add':
      return `新增${title}`;
    case 'edit':
      return `编辑${title}`;
    case 'view':
      return `查看${title}`;
    default:
      return title;
  }
});

async function handleSubmit() {
  if (basicFormRef.value !== null) {
    if (basicFormRef.value.model === null) {
      return;
    }
    try {
      basicFormRef.value.action = 'submit';
      await basicFormRef.value.validate();
      switch (props.operation) {
        case 'add':
          if (props.saveFn) {
            // eslint-disable-next-line max-depth
            try {
              basicFormRef.value.action = 'submit';
              basicFormRef.value.startLoading();
              const submitModel = basicFormRef.value.getSubmitModel();
              const result = await props.saveFn?.(submitModel);
              // eslint-disable-next-line max-depth
              if (result?.error === null) {
                // eslint-disable-next-line max-depth
                if (props.showMessageOnSuccess) {
                  window.$message?.success('保存成功');
                }
                emit('save', basicFormRef.value.model);
                closeDrawer();
              }
            } finally {
              basicFormRef.value.endLoading();
              basicFormRef.value.action = null;
            }
          } else {
            emit('save', basicFormRef.value.model);
          }
          break;
        case 'edit':
          if (props.updateFn) {
            // eslint-disable-next-line max-depth
            try {
              basicFormRef.value.action = 'submit';
              basicFormRef.value.startLoading();
              const submitModel = basicFormRef.value.getSubmitModel();
              const result = await props.updateFn?.(submitModel as { id: string; [key: string]: any });
              // eslint-disable-next-line max-depth
              if (result?.error === null) {
                // eslint-disable-next-line max-depth
                if (props.showMessageOnSuccess) {
                  window.$message?.success('更新成功');
                }
                emit('update', basicFormRef.value.model);
                closeDrawer();
              }
            } finally {
              basicFormRef.value.endLoading();
              basicFormRef.value.action = null;
            }
          } else {
            emit('update', basicFormRef.value.model);
          }
          break;
        default:
          break;
      }
    } catch {
      // window.$message?.error(error);
    }
  }
}

function closeDrawer() {
  show.value = false;
  emit('close');
}

defineExpose({
  show,
  model: basicFormRef.value?.model,
  validate: basicFormRef.value?.validate,
  restoreValidation: basicFormRef.value?.restoreValidation,
  closeDrawer
});
</script>

<template>
  <NModal v-model:show="show" :title="finalTitle" v-bind="finalModalConfig" @close="closeDrawer">
    <BasicForm ref="basicFormRef" v-model:show="show" v-bind="props" :show-title="false">
      <!-- @vue-skip -->
      <template v-for="(_, name) in $slots" #[name]="data" :key="name">
        <slot :name="name" v-bind="data"></slot>
      </template>
    </BasicForm>
    <template v-if="operation !== 'view' && !basicFormRef?.loading" #action>
      <NSpace :size="16">
        <NButton @click="closeDrawer">取消</NButton>
        <NButton type="primary" @click="handleSubmit">确认</NButton>
      </NSpace>
    </template>
  </NModal>
</template>

<style scoped></style>
